import React, { useState } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import TimeRangeSelector from "../../components/helpers/TimeRangeSelector";

import { useTranslation } from "react-i18next";
import { maskString, maskFullNames } from "../../components/helpers/maskHelpers";

import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";
import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  margin: "0 auto",
};

export default function DriverToFromPayersReport() {
  const { t } = useTranslation();

  const [summary, setSummary] = useState([]);
  const [message, setMessage] = useState("");
  const [messageVariant, setMessageVariant] = useState("info");
  const [timeRange, setTimeRange] = useState({ start: "", end: "" });
  const [filterText, setFilterText] = useState("");

  const fetchReport = async () => {
    if (!timeRange.start || !timeRange.end) return;

    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/driver/payer-transactions-in-time`,
        { params: { start: timeRange.start, end: timeRange.end } }
      );

      const rows = Array.isArray(res.data) ? res.data : [];
      const payerRows = rows.filter((item) => item.payer_direction != null);

      setSummary(payerRows);

      if (payerRows.length === 0) {
        setMessage(t("driver_to_from_payers_report.messages.empty"));
        setMessageVariant("info");
      } else {
        setMessage("");
        setMessageVariant("info");
      }
    } catch (err) {
      console.error("Failed to fetch summary", err);
      setSummary([]);
      setMessage(t("driver_to_from_payers_report.messages.fetch_failed"));
      setMessageVariant("error");
    }
  };

  const normalizedFilter = filterText.trim().toLowerCase();

  const payerSummary = summary.filter((item) => item.payer_direction != null);

 

const filteredSummary = normalizedFilter
  ? payerSummary.filter((item) => {
      const searchableText = [
        item.from_ownerfullname,
        item.to_ownerfullname,

        item.from_sponsor,
        item.to_sponsor,

        item.payer_route,

        item.start_point,
        item.end_point,

        item.from_start_point,
        item.from_end_point,
        item.to_start_point,
        item.to_end_point,

        item.from_owneruserno,
        item.to_owneruserno,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedFilter);
    })
  : payerSummary;


  return (
    <StandardScreen
      topNav={<TopNav title={t("driver_to_from_payers_report.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="4">
            <Card title={t("driver_to_from_payers_report.title")}>
              <Stack gap="3">
                <FormField>
                  <div>
                    <TimeRangeSelector onRangeChange={setTimeRange} />
                  </div>
                </FormField>

                <FormField label={t("driver_to_from_payers_report.fields.search.label")}>
                  <TextInput
                    type="text"
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    placeholder={t("driver_to_from_payers_report.fields.search.placeholder")}
                  />
                </FormField>

                <Button onClick={fetchReport} fullWidth>
                  {t("driver_to_from_payers_report.actions.get_report")}
                </Button>

                <StatusMessage variant={messageVariant} message={message} />
              </Stack>
            </Card>

            {filteredSummary.length > 0 && (
              <Card>
                <TableShell maxHeight="65vh">
                  <Table>
                    <thead>
                      <tr>
                        <Th columnIndex={0} sticky wrap wider>{t("driver_to_from_payers_report.table.time")}</Th>
                        <Th columnIndex={1} sticky wrap wider>{t("driver_to_from_payers_report.table.fullname")}</Th>
                        <Th>{t("driver_to_from_payers_report.table.request")}</Th>
                        <Th>{t("driver_to_from_payers_report.table.userno")}</Th>
                        <Th>{t("driver_to_from_payers_report.table.sponsor")}</Th>
                        <Th>{t("driver_to_from_payers_report.table.wallet")}</Th>
                        <Th align="right">{t("driver_to_from_payers_report.table.tickets")}</Th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredSummary.map((item, index) => (
                        <tr key={`${item.treq_id}-${index}`}>
                          <Td columnIndex={0} sticky wrap wider>
                            {new Date(item.created_at)
                              .toLocaleString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })
                              .replace(",", "")}
                          </Td>

                          <Td columnIndex={1} sticky wrap wider>{maskFullNames(item.payer_fullname)}</Td>
                          <Td>{t(`driver_to_from_payers_report.table.${item.request}`)}</Td>
                          <Td>{maskString(item.payer_userno)}</Td>
                          <Td>{maskFullNames(item.payer_sponsor)}</Td>
                          <Td>{item.payer_route}</Td>
                          <NumberTd>{item.payer_delta_tickets}</NumberTd>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableShell>
              </Card>
            )}
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}